import { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';
import { useSSEStore } from '../../stores/sseStore';
import { usePagination } from '../../hooks/usePagination';
import { Pagination } from '../../components/shared/Pagination';
import { DollarSign, TrendingUp, TrendingDown, Package, AlertTriangle, ChevronDown, ChevronUp } from 'lucide-react';
import { PnLSection } from './PnLSection';
import { PendingClaimsSection } from './PendingClaimsSection';
import { MpReconciliationSection } from './MpReconciliationSection';
import { CancellationsSection } from './CancellationsSection';
import { PeriodBar } from './PeriodBar';
import { useFinanceFiltersStore, periodToQs } from '../../stores/financeFiltersStore';
import { useCan, visibleTabs, pickActiveTab, NoAccess } from '../../lib/rbac';
import { DailyRowWithDrilldown } from './DailyDrilldown';
import type { FinanceDailyRow } from './types';

type Tab = 'summary' | 'monthly' | 'daily' | 'top' | 'sites' | 'amazonSpend' | 'pnl' | 'ledger' | 'pendingClaims' | 'mpRecon' | 'cancellations';

/* ------------------------------------------------------------------ */
/*  CSS Bar Chart (reusable)                                          */
/* ------------------------------------------------------------------ */
function BarChart({
  data,
  labelKey,
  bars,
  formatLabel,
  formatValue,
  showEveryNth = 1,
}: {
  data: any[];
  labelKey: string;
  bars: { key: string; color: string; negColor?: string; label: string }[];
  formatLabel: (v: string) => string;
  formatValue: (n: number) => string;
  showEveryNth?: number;
}) {
  const { t } = useTranslation();
  const [tooltip, setTooltip] = useState<{ idx: number; x: number; y: number } | null>(null);

  if (!data.length) return null;

  // Compute max across all bar keys (absolute values for negative support)
  const allValues = data.flatMap(d => bars.map(b => Math.abs(Number(d[b.key]) || 0)));
  const maxVal = Math.max(...allValues, 1);

  const barHeight = (val: number) => Math.max((Math.abs(val) / maxVal) * 100, 1);

  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4">
      {/* Legend */}
      <div className="flex items-center gap-4 mb-3">
        <h3 className="text-sm font-medium text-[var(--color-text)]">{t('finance.chartRevenue')}</h3>
        <div className="flex items-center gap-3 ml-auto text-xs text-[var(--color-text-secondary)]">
          {bars.map(b => (
            <span key={b.key} className="flex items-center gap-1">
              <span className="w-3 h-3 rounded-sm inline-block" style={{ backgroundColor: b.color }} />
              {b.label}
            </span>
          ))}
        </div>
        <span className="text-xs text-[var(--color-text-secondary)]">
          {t('finance.maxValue')}: {formatValue(maxVal)}
        </span>
      </div>

      {/* Chart area */}
      <div className="relative overflow-x-auto">
        <div className="flex items-end gap-[2px] min-w-0" style={{ minHeight: '180px' }}>
          {data.map((d, idx) => (
            <div
              key={idx}
              className="flex-1 min-w-[8px] max-w-[40px] flex flex-col items-center relative group cursor-pointer"
              onMouseEnter={(e) => {
                const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                setTooltip({ idx, x: rect.left + rect.width / 2, y: rect.top });
              }}
              onMouseLeave={() => setTooltip(null)}
            >
              {/* Bars stacked side by side */}
              <div className="flex items-end gap-[1px] w-full" style={{ height: '160px' }}>
                {bars.map(b => {
                  const val = Number(d[b.key]) || 0;
                  const isNeg = val < 0;
                  const bg = isNeg && b.negColor ? b.negColor : b.color;
                  return (
                    <div
                      key={b.key}
                      className="flex-1 rounded-t-sm transition-all duration-150"
                      style={{
                        height: `${barHeight(val)}%`,
                        backgroundColor: bg,
                        opacity: tooltip?.idx === idx ? 1 : 0.8,
                      }}
                    />
                  );
                })}
              </div>
              {/* X label */}
              {idx % showEveryNth === 0 ? (
                <span className="text-[9px] text-[var(--color-text-secondary)] mt-1 whitespace-nowrap leading-none">
                  {formatLabel(d[labelKey])}
                </span>
              ) : (
                <span className="mt-1 h-[11px]" />
              )}
            </div>
          ))}
        </div>

        {/* Tooltip */}
        {tooltip !== null && data[tooltip.idx] && (
          <div
            className="fixed z-50 bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg shadow-lg p-2.5 text-xs pointer-events-none"
            style={{ left: tooltip.x, top: tooltip.y - 8, transform: 'translate(-50%, -100%)' }}
          >
            <p className="font-medium text-[var(--color-text)] mb-1">
              {formatLabel(data[tooltip.idx][labelKey])}
            </p>
            {bars.map(b => {
              const val = Number(data[tooltip.idx][b.key]) || 0;
              return (
                <div key={b.key} className="flex items-center gap-2 justify-between">
                  <span className="flex items-center gap-1">
                    <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: b.color }} />
                    {b.label}
                  </span>
                  <span className="font-mono font-medium ml-3">{formatValue(val)}</span>
                </div>
              );
            })}
            {data[tooltip.idx].avg_margin != null && (
              <div className="flex justify-between mt-0.5 pt-0.5 border-t border-[var(--color-border)]">
                <span className="text-[var(--color-text-secondary)]">Margen</span>
                <span className={`font-medium ${Number(data[tooltip.idx].avg_margin) >= 10 ? 'text-green-600' : Number(data[tooltip.idx].avg_margin) >= 0 ? 'text-yellow-600' : 'text-red-500'}`}>
                  {Number(data[tooltip.idx].avg_margin).toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Margin Distribution Card                                          */
/* ------------------------------------------------------------------ */
function MarginDistribution({ data }: { data: { high: number; medium: number; low: number; negative: number } }) {
  const { t } = useTranslation();
  const total = data.high + data.medium + data.low + data.negative || 1;

  const segments = [
    { key: 'high', label: t('finance.marginHigh'), count: data.high, color: 'bg-green-500', text: 'text-green-600' },
    { key: 'medium', label: t('finance.marginMedium'), count: data.medium, color: 'bg-blue-500', text: 'text-blue-600' },
    { key: 'low', label: t('finance.marginLow'), count: data.low, color: 'bg-yellow-500', text: 'text-yellow-600' },
    { key: 'negative', label: t('finance.marginNegative'), count: data.negative, color: 'bg-red-500', text: 'text-red-500' },
  ];

  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4">
      <h3 className="text-sm font-medium text-[var(--color-text)] mb-3">{t('finance.marginDistribution')}</h3>

      {/* Stacked bar */}
      <div className="flex rounded-full overflow-hidden h-4 mb-3">
        {segments.map(s => (
          s.count > 0 ? (
            <div
              key={s.key}
              className={`${s.color} transition-all`}
              style={{ width: `${(s.count / total) * 100}%` }}
              title={`${s.label}: ${s.count}`}
            />
          ) : null
        ))}
      </div>

      {/* Legend rows */}
      <div className="grid grid-cols-2 gap-2">
        {segments.map(s => (
          <div key={s.key} className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1.5">
              <span className={`w-2.5 h-2.5 rounded-sm ${s.color}`} />
              <span className="text-[var(--color-text-secondary)]">{s.label}</span>
            </span>
            <span className={`font-bold ${s.text}`}>{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Negative Margin Alert                                             */
/* ------------------------------------------------------------------ */
function NegativeMarginAlert({ count, sellerId }: { count: number; sellerId: number }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const loadOrders = async () => {
    if (orders.length) { setExpanded(!expanded); return; }
    setLoading(true);
    const res = await api.get<any[]>(`/finance/negative-margin-orders?seller_id=${sellerId}`);
    if (res.success && res.data) setOrders(res.data);
    setExpanded(true);
    setLoading(false);
  };

  const fmt = (n: number) => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  const fmtDay = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  return (
    <div className="bg-red-50 dark:bg-red-950/30 border border-red-300 dark:border-red-800 rounded-lg p-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-red-500" />
          <div>
            <p className="text-sm font-bold text-red-700 dark:text-red-400">{t('finance.negativeAlertTitle')}</p>
            <p className="text-xs text-red-600 dark:text-red-500">{count} {t('finance.negativeAlertDesc')}</p>
          </div>
        </div>
        <button
          onClick={loadOrders}
          className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-700 dark:text-red-400 bg-red-100 dark:bg-red-900/40 rounded-lg hover:bg-red-200 dark:hover:bg-red-900/60 transition-colors"
        >
          {loading ? (
            <div className="w-3 h-3 border-2 border-red-500 border-t-transparent rounded-full animate-spin" />
          ) : expanded ? (
            <>{t('finance.hideNegativeOrders')} <ChevronUp className="w-3 h-3" /></>
          ) : (
            <>{t('finance.showNegativeOrders')} <ChevronDown className="w-3 h-3" /></>
          )}
        </button>
      </div>

      {expanded && orders.length > 0 && (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-left text-red-600 dark:text-red-400 border-b border-red-200 dark:border-red-800">
                <th className="pb-1.5 pr-2 font-medium">{t('finance.day')}</th>
                <th className="pb-1.5 pr-2 font-medium">Orden ML</th>
                <th className="pb-1.5 pr-2 font-medium">Producto</th>
                <th className="pb-1.5 pr-2 font-medium">ASIN</th>
                <th className="pb-1.5 pr-2 font-medium">Site</th>
                <th className="pb-1.5 pr-2 font-medium text-right">Venta</th>
                <th className="pb-1.5 pr-2 font-medium text-right">Costo</th>
                <th className="pb-1.5 pr-2 font-medium text-right">{t('finance.profit')}</th>
                <th className="pb-1.5 font-medium text-right">{t('finance.margin')}</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o: any, i: number) => (
                <tr key={i} className="border-b border-red-100 dark:border-red-900/40 hover:bg-red-100/50 dark:hover:bg-red-900/20 text-red-800 dark:text-red-300">
                  <td className="py-1.5 pr-2 whitespace-nowrap">{o.date_created ? fmtDay(o.date_created) : '-'}</td>
                  <td className="py-1.5 pr-2 font-mono whitespace-nowrap">{String(o.ml_order_id || '-')}</td>
                  <td className="py-1.5 pr-2" title={o.title}>{o.title || '-'}</td>
                  <td className="py-1.5 pr-2 font-mono">{String(o.asin || '-')}</td>
                  <td className="py-1.5 pr-2">{String(o.site || '-')}</td>
                  <td className="py-1.5 pr-2 text-right font-mono whitespace-nowrap">{fmt(o.paid_amount)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono whitespace-nowrap">{fmt(o.cost)}</td>
                  <td className="py-1.5 pr-2 text-right font-mono text-red-600 dark:text-red-400 font-bold whitespace-nowrap">{fmt(o.profit)}</td>
                  <td className="py-1.5 text-right font-bold text-red-600 dark:text-red-400 whitespace-nowrap">{o.margin != null ? `${Number(o.margin).toFixed(1)}%` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main Page                                                         */
/* ------------------------------------------------------------------ */
export default function FinancePage() {
  const { t } = useTranslation();
  const can = useCan();
  const [tab, setTab] = useState<Tab>('summary');
  const [summary, setSummary] = useState<any>(null);
  const [monthly, setMonthly] = useState<any[]>([]);
  const [daily, setDaily] = useState<any[]>([]);
  const [topProducts, setTopProducts] = useState<any[]>([]);
  const [sites, setSites] = useState<any[]>([]);
  const [marginDist, setMarginDist] = useState<any>(null);
  const [amzSpend, setAmzSpend] = useState<any>(null);
  const [amzSpendProducts, setAmzSpendProducts] = useState<any[]>([]);
  const [pnlData, setPnlData] = useState<any[]>([]);
  const [ledgerSummary, setLedgerSummary] = useState<any>(null);
  const [ledgerDaily, setLedgerDaily] = useState<any[]>([]);
  const [ledgerEntries, setLedgerEntries] = useState<any[]>([]);
  const ledgerPag = usePagination('finance-ledger');
  const [ledgerFilter, setLedgerFilter] = useState('');
  const { sellerId } = useUIStore();
  // Todas las pestañas leen el período del store global `useFinanceFiltersStore`
  // (Fase 2/3/4). Sin selectores locales de `days`.
  const period = useFinanceFiltersStore(s => s.period);
  const [loading, setLoading] = useState(false);

  const tabs: { key: Tab; label: string }[] = [
    { key: 'summary', label: t('finance.summary') },
    { key: 'monthly', label: t('finance.monthly') },
    { key: 'daily', label: t('finance.daily') },
    { key: 'top', label: t('finance.topProducts') },
    { key: 'sites', label: t('finance.bySite') },
    { key: 'amazonSpend', label: t('finance.amazonSpend') },
    { key: 'pnl', label: t('finance.pnl') },
    { key: 'ledger', label: 'Profit Ledger' },
    { key: 'mpRecon', label: 'Conciliación MP' },
    { key: 'cancellations', label: 'Cancelaciones' },
    { key: 'pendingClaims', label: 'Reclamos pendientes' },
  ];

  const allowedTabs = visibleTabs('finance', tabs, can);
  // Pestaña EFECTIVA (la elegida si está permitida; si no, la primera permitida).
  // Todo lo que pide datos usa ésta, nunca la cruda: así no se dispara ni un pedido
  // de una pestaña vedada en el primer render.
  const active = pickActiveTab('finance', tabs, tab, can) as Tab | null;
  useEffect(() => {
    if (active && active !== tab) setTab(active);
  }, [active, tab]);

  const loadData = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    const sid = `seller_id=${sellerId}`;
    // Query string del período global (presets → `days=N`; mtd/custom → `from&to`).
    const periodQs = periodToQs(useFinanceFiltersStore.getState().getQuery());

    if (active === 'summary') {
      const [sumRes, siteRes, distRes] = await Promise.all([
        api.get<any>(`/finance/summary?${sid}&${periodQs}`),
        api.get<any[]>(`/finance/by-site?${sid}&${periodQs}`),
        api.get<any>(`/finance/margin-distribution?${sid}&${periodQs}`),
      ]);
      if (sumRes.success && sumRes.data) setSummary(sumRes.data);
      if (siteRes.success && siteRes.data) setSites(siteRes.data);
      if (distRes.success && distRes.data) setMarginDist(distRes.data);
    } else if (active === 'monthly') {
      const res = await api.get<any[]>(`/finance/monthly?${sid}&${periodQs}`);
      if (res.success && res.data) setMonthly(res.data);
    } else if (active === 'daily') {
      // Fase 4: lee el período del store global (mismo `periodQs` que el resto).
      const res = await api.get<any[]>(`/finance/daily?${sid}&${periodQs}`);
      if (res.success && res.data) setDaily(res.data);
    } else if (active === 'top') {
      const res = await api.get<any[]>(`/finance/top-products?${sid}&limit=20&${periodQs}`);
      if (res.success && res.data) setTopProducts(res.data);
    } else if (active === 'sites') {
      const res = await api.get<any[]>(`/finance/by-site?${sid}&${periodQs}`);
      if (res.success && res.data) setSites(res.data);
    } else if (active === 'amazonSpend') {
      const [spendRes, prodRes] = await Promise.all([
        api.get<any>(`/finance/amazon-spend?${sid}&${periodQs}`),
        api.get<any[]>(`/finance/amazon-spend/by-product?${sid}&limit=30&${periodQs}`),
      ]);
      if (spendRes.success && spendRes.data) setAmzSpend(spendRes.data);
      if (prodRes.success && prodRes.data) setAmzSpendProducts(prodRes.data);
    } else if (active === 'pnl') {
      const res = await api.get<any[]>(`/finance/pnl-reconciliation?${sid}&limit=100&${periodQs}`);
      if (res.success && res.data) setPnlData(res.data);
    } else if (active === 'ledger') {
      const [sumRes, dailyRes, entriesRes] = await Promise.all([
        api.get<any>(`/v3/finance/ledger/summary?${sid}&${periodQs}`),
        api.get<any[]>(`/v3/finance/ledger/daily?${sid}&${periodQs}`),
        api.get<any>(`/v3/finance/ledger/entries?${sid}&limit=${ledgerPag.perPage}&offset=${(ledgerPag.page - 1) * ledgerPag.perPage}${ledgerFilter ? `&event_type=${ledgerFilter}` : ''}`),
      ]);
      if (sumRes.success && sumRes.data) setLedgerSummary(sumRes.data);
      if (dailyRes.success && dailyRes.data) setLedgerDaily(dailyRes.data);
      if (entriesRes.success) {
        setLedgerEntries((entriesRes as any).rows || []);
        ledgerPag.setTotal((entriesRes as any).total || 0);
      }
    }
    setLoading(false);
  // `period` como dependencia: cuando el PeriodBar global cambia, se re-fetchea.
  }, [active, sellerId, period, ledgerPag.page, ledgerPag.perPage, ledgerFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  // Cuando cambia el período global, resetear paginación del ledger (los offsets
  // viejos pueden quedar fuera del nuevo rango).
  useEffect(() => { ledgerPag.resetPage(); }, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  // SSE real-time updates
  const { subscribe } = useSSEStore();
  const loadDataRef = useRef(loadData);
  loadDataRef.current = loadData;
  useEffect(() => {
    const refresh = () => loadDataRef.current();
    const unsubs = [
      subscribe('stock:po-created', refresh),
      subscribe('stock:po-confirmed', refresh),
      subscribe('warehouse:item_closed', refresh),
    ];
    return () => unsubs.forEach(u => u());
  }, [subscribe]);

  const fmt = (n: number) => n != null ? `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '-';
  const fmtPct = (n: number) => n != null ? `${Number(n).toFixed(1)}%` : '-';
  const fmtMonth = (d: string) => {
    const date = new Date(d);
    return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };
  const fmtDay = (d: string) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  // Prepare chart data: sorted ascending for visual left-to-right timeline
  const dailyChartData = [...daily].reverse();
  const monthlyChartData = [...monthly].reverse();

  if (active === null) return <NoAccess />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-xl sm:text-2xl font-bold text-[var(--color-text)]">{t('finance.title')}</h1>
      </div>

      {/* Filtro global de período — Fase 2 refactor. Los sub-componentes lo
          van a leer en Fase 3 (todavía cada tab usa su selector viejo). */}
      <PeriodBar />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[var(--color-border)] overflow-x-auto">
        {allowedTabs.map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              active === tb.key ? 'border-[var(--color-primary)] text-[var(--color-primary)]'
                : 'border-transparent text-[var(--color-text-secondary)]'
            }`}>
            {tb.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-3 border-[var(--color-primary)] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <>
          {/* Summary */}
          {active === 'summary' && summary && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <BigCard icon={<DollarSign />} label={t('finance.revenue')} value={fmt(summary.total_revenue)} color="text-blue-600" />
                <BigCard icon={<TrendingDown />} label={t('finance.fees')} value={fmt(summary.total_fees)} color="text-orange-500" />
                <BigCard icon={<TrendingDown />} label={t('finance.cost')} value={fmt(summary.total_cost)} color="text-red-500" />
                <BigCard icon={<TrendingUp />} label={t('finance.profit')} value={fmt(summary.total_profit)}
                  color={summary.total_profit >= 0 ? 'text-green-600' : 'text-red-500'} />
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <StatCard label={t('finance.orders')} value={summary.total_orders} />
                <StatCard label={t('finance.avgMargin')} value={fmtPct(summary.avg_margin)}
                  color={summary.avg_margin >= 10 ? 'text-green-600' : summary.avg_margin >= 0 ? 'text-yellow-600' : 'text-red-500'} />
                <StatCard label={t('finance.netIncome')} value={fmt(summary.total_net)} color="text-blue-600" />
                <StatCard label={t('finance.negativeMargin')} value={summary.negative_margin_count}
                  color={summary.negative_margin_count > 0 ? 'text-red-500' : 'text-green-600'}
                  icon={summary.negative_margin_count > 0 ? <AlertTriangle className="w-4 h-4" /> : undefined} />
              </div>

              {/* Negative margin alert */}
              {summary.negative_margin_count > 0 && (
                <NegativeMarginAlert count={summary.negative_margin_count} sellerId={sellerId} />
              )}

              {/* Margin distribution */}
              {marginDist && <MarginDistribution data={marginDist} />}

              {/* Sites mini-table */}
              {sites.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-[var(--color-text)] mb-3">{t('finance.bySite')}</h3>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    {sites.map((s: any) => (
                      <div key={s.site} className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
                        <p className="text-sm font-bold text-[var(--color-primary)]">{s.site || 'N/A'}</p>
                        <p className="text-xs text-[var(--color-text-secondary)] mt-1">{s.orders} {t('finance.orders')}</p>
                        <p className="text-sm font-medium mt-1">{fmt(s.revenue)}</p>
                        <p className={`text-xs font-medium ${s.profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {fmt(s.profit)} ({fmtPct(s.avg_margin)})
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Monthly */}
          {active === 'monthly' && (
            <div className="space-y-4">
              {/* Monthly bar chart */}
              {monthlyChartData.length > 0 && (
                <BarChart
                  data={monthlyChartData}
                  labelKey="month"
                  bars={[
                    { key: 'revenue', color: '#3b82f6', label: t('finance.revenue') },
                    { key: 'profit', color: '#22c55e', negColor: '#ef4444', label: t('finance.profit') },
                  ]}
                  formatLabel={fmtMonth}
                  formatValue={fmt}
                  showEveryNth={1}
                />
              )}

              <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
                    <tr>
                      <th className="text-left px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.month')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.orders')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.revenue')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.fees')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.cost')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.profit')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.margin')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {monthly.map((m: any) => (
                      <tr key={m.month} className="hover:bg-[var(--color-bg-secondary)]">
                        <td className="px-4 py-3 font-medium">{fmtMonth(m.month)}</td>
                        <td className="px-4 py-3 text-right">{m.orders}</td>
                        <td className="px-4 py-3 text-right font-mono">{fmt(m.revenue)}</td>
                        <td className="px-4 py-3 text-right font-mono text-orange-500">{fmt(m.fees)}</td>
                        <td className="px-4 py-3 text-right font-mono text-red-500">{fmt(m.cost)}</td>
                        <td className={`px-4 py-3 text-right font-mono font-bold ${m.profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {fmt(m.profit)}
                        </td>
                        <td className={`px-4 py-3 text-right font-medium ${m.avg_margin >= 10 ? 'text-green-600' : m.avg_margin >= 0 ? 'text-yellow-600' : 'text-red-500'}`}>
                          {fmtPct(m.avg_margin)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Daily — Fase 4: período del store global + filas con drill-down */}
          {active === 'daily' && (
            <div className="space-y-4">
              {/* Daily bar chart */}
              {dailyChartData.length > 0 && (
                <BarChart
                  data={dailyChartData}
                  labelKey="day"
                  bars={[
                    { key: 'revenue', color: '#3b82f6', label: t('finance.revenue') },
                    { key: 'profit', color: '#22c55e', negColor: '#ef4444', label: t('finance.profit') },
                  ]}
                  formatLabel={fmtDay}
                  formatValue={fmt}
                  showEveryNth={Math.max(1, Math.floor(dailyChartData.length / 6))}
                />
              )}

              <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
                    <tr>
                      <th className="w-8 px-2 py-3" />
                      <th className="text-left px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.day')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.orders')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.revenue')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.profit')}</th>
                      <th className="text-right px-4 py-3 font-medium text-[var(--color-text-secondary)]">{t('finance.margin')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--color-border)]">
                    {daily.length === 0 && (
                      <tr>
                        <td colSpan={6} className="px-4 py-6 text-center text-xs text-[var(--color-text-secondary)]">
                          Sin datos para este período.
                        </td>
                      </tr>
                    )}
                    {daily.map((d: FinanceDailyRow) => (
                      <DailyRowWithDrilldown
                        // `key` incluye el período para que al cambiarlo se
                        // descarte el estado local (drilldowns abiertos).
                        key={`${d.day}-${JSON.stringify(period)}`}
                        row={d}
                        sellerId={sellerId}
                        formatMoney={fmt}
                        formatPct={fmtPct}
                        formatDay={fmtDay}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Top Products */}
          {active === 'top' && (
            <div className="overflow-x-auto border border-[var(--color-border)] rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-[var(--color-bg-secondary)] border-b border-[var(--color-border)]">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">#</th>
                    <th className="text-left px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('catalog.product')}</th>
                    <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('finance.totalSold')}</th>
                    <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('finance.revenue')}</th>
                    <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('finance.profit')}</th>
                    <th className="text-right px-3 py-2 font-medium text-[var(--color-text-secondary)]">{t('finance.margin')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--color-border)]">
                  {topProducts.map((p: any, i: number) => (
                    <tr key={p.asin} className="hover:bg-[var(--color-bg-secondary)]">
                      <td className="px-3 py-2 text-[var(--color-text-secondary)]">{i + 1}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {p.product_image ? (
                            <img src={p.product_image} alt="" className="w-10 h-10 rounded object-cover bg-white flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded bg-[var(--color-bg-secondary)] flex items-center justify-center flex-shrink-0">
                              <Package className="w-5 h-5 text-[var(--color-text-secondary)]" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="text-xs font-medium">{p.title || p.asin}</p>
                            <p className="text-xs text-[var(--color-text-secondary)] font-mono">{p.asin}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right font-bold">{p.total_sold}</td>
                      <td className="px-3 py-2 text-right font-mono">{fmt(p.total_revenue)}</td>
                      <td className={`px-3 py-2 text-right font-mono font-bold ${p.total_profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                        {fmt(p.total_profit)}
                      </td>
                      <td className={`px-3 py-2 text-right font-medium ${p.avg_margin >= 10 ? 'text-green-600' : p.avg_margin >= 0 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {fmtPct(p.avg_margin)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Sites */}
          {active === 'sites' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {sites.map((s: any) => (
                <div key={s.site} className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-5">
                  <h3 className="text-lg font-bold text-[var(--color-primary)]">{s.site || 'N/A'}</h3>
                  <div className="mt-3 space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">{t('finance.orders')}</span>
                      <span className="font-medium">{s.orders}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">{t('finance.revenue')}</span>
                      <span className="font-mono font-medium">{fmt(s.revenue)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">{t('finance.profit')}</span>
                      <span className={`font-mono font-bold ${s.profit >= 0 ? 'text-green-600' : 'text-red-500'}`}>{fmt(s.profit)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">{t('finance.margin')}</span>
                      <span className={`font-medium ${s.avg_margin >= 10 ? 'text-green-600' : s.avg_margin >= 0 ? 'text-yellow-600' : 'text-red-500'}`}>
                        {fmtPct(s.avg_margin)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Amazon Spend Tab */}
          {active === 'amazonSpend' && amzSpend && (
            <div className="space-y-6">
              {/* Summary cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <BigCard icon={<DollarSign className="w-5 h-5" />} label={t('finance.totalCharged')} value={fmt(amzSpend.total_charged)} color="text-red-500" />
                <BigCard icon={<TrendingDown className="w-5 h-5" />} label={t('finance.totalRefunded')} value={fmt(amzSpend.total_refunded)} color="text-green-500" />
                <BigCard icon={<DollarSign className="w-5 h-5" />} label={t('finance.netSpend')} value={fmt(amzSpend.net_spend)} color="text-[var(--color-text)]" />
                <BigCard icon={<Package className="w-5 h-5" />} label={t('finance.charges')} value={String(amzSpend.total_charges)} color="text-[var(--color-primary)]" />
              </div>

              {/* Top products by spend */}
              <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg overflow-hidden">
                <div className="px-4 py-3 border-b border-[var(--color-border)]">
                  <h3 className="text-sm font-medium text-[var(--color-text)]">{t('finance.spendByProduct')}</h3>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-xs text-[var(--color-text-secondary)] border-b border-[var(--color-border)]">
                        <th className="text-left py-2 px-4">{t('finance.topProducts')}</th>
                        <th className="text-left py-2 px-2">ASIN</th>
                        <th className="text-right py-2 px-2">{t('finance.qty')}</th>
                        <th className="text-right py-2 px-2">{t('finance.totalSpent')}</th>
                        <th className="text-right py-2 px-2">{t('finance.shipping')}</th>
                        <th className="text-right py-2 px-4">{t('finance.orderCount')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {amzSpendProducts.map((p: any, i: number) => (
                        <tr key={i} className="border-b border-[var(--color-border)] hover:bg-[var(--color-bg-tertiary)]">
                          <td className="py-2 px-4">
                            <div className="flex items-center gap-2">
                              {p.product_image && <img src={p.product_image} alt="" className="w-8 h-8 rounded object-cover" />}
                              <span className="text-[var(--color-text)]">{(p.title || '').slice(0, 50)}</span>
                            </div>
                          </td>
                          <td className="py-2 px-2 font-mono text-xs text-[var(--color-text-secondary)]">{p.asin}</td>
                          <td className="py-2 px-2 text-right text-[var(--color-text)]">{p.total_qty}</td>
                          <td className="py-2 px-2 text-right font-mono font-medium text-red-500">{fmt(p.total_spent)}</td>
                          <td className="py-2 px-2 text-right font-mono text-[var(--color-text-secondary)]">{fmt(p.total_shipping)}</td>
                          <td className="py-2 px-4 text-right text-[var(--color-text)]">{p.order_count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

          {/* P&L V3 — Waterfall + Margins + Reconciliation */}
          {active === 'pnl' && <PnLSection />}

          {active === 'pendingClaims' && <PendingClaimsSection />}

          {active === 'mpRecon' && <MpReconciliationSection />}

          {active === 'cancellations' && <CancellationsSection />}

          {/* Profit Ledger */}
          {active === 'ledger' && (
            <div className="space-y-6">
              {/* El selector local de período fue removido (Fase 3): ahora lee del
                  PeriodBar global en el header. */}

              {/* Summary cards */}
              {ledgerSummary && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <BigCard icon={<DollarSign />} label="Net Profit"
                      value={fmt(ledgerSummary.net_profit)}
                      color={ledgerSummary.net_profit >= 0 ? 'text-green-600' : 'text-red-500'} />
                    {ledgerSummary.breakdown?.map((b: any) => {
                      const isPositive = parseFloat(b.total) >= 0;
                      return (
                        <div key={b.event_type} className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
                          <div className="text-xs text-[var(--color-text-secondary)] uppercase">{b.event_type.replace(/_/g, ' ')}</div>
                          <div className={`text-lg font-bold ${isPositive ? 'text-green-600' : 'text-red-500'}`}>
                            {fmt(parseFloat(b.total))}
                          </div>
                          <div className="text-xs text-[var(--color-text-muted)]">{b.count} entries</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Daily chart */}
              {ledgerDaily.length > 0 && (
                <BarChart
                  data={[...ledgerDaily].reverse()}
                  labelKey="day"
                  bars={[
                    { key: 'revenue', color: 'var(--color-success)', label: 'Revenue' },
                    { key: 'cogs', color: 'var(--color-error)', negColor: 'var(--color-error)', label: 'COGS' },
                    { key: 'net_profit', color: 'var(--color-primary)', negColor: 'var(--color-warning)', label: 'Net Profit' },
                  ]}
                  formatLabel={fmtDay}
                  formatValue={fmt}
                />
              )}

              {/* Entries table */}
              <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg">
                <div className="flex items-center justify-between p-4 border-b border-[var(--color-border)]">
                  <h3 className="text-sm font-medium text-[var(--color-text)]">
                    Ledger Entries ({ledgerPag.total.toLocaleString()})
                  </h3>
                  <select value={ledgerFilter} onChange={e => { setLedgerFilter(e.target.value); ledgerPag.resetPage(); }}
                    className="text-xs bg-[var(--color-surface)] border border-[var(--color-border)] rounded px-2 py-1 text-[var(--color-text)]">
                    <option value="">All types</option>
                    {['sale','ml_fee','ml_shipping','amazon_cost','amazon_shipping','stock_cost',
                      'stock_cost_reversal',
                      'sale_cancelled','ml_fee_refund','claim_loss','amazon_return',
                      'cost_transfer_in','cost_transfer_out','adjustment'].map(t => (
                      <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>
                    ))}
                  </select>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="bg-[var(--color-surface)]">
                        <th className="text-left p-2 text-[var(--color-text-secondary)]">Date</th>
                        <th className="text-left p-2 text-[var(--color-text-secondary)]">Type</th>
                        <th className="text-right p-2 text-[var(--color-text-secondary)]">Amount</th>
                        <th className="text-left p-2 text-[var(--color-text-secondary)]">Order</th>
                        <th className="text-left p-2 text-[var(--color-text-secondary)]">Description</th>
                        <th className="text-left p-2 text-[var(--color-text-secondary)]">Source</th>
                      </tr>
                    </thead>
                    <tbody>
                      {ledgerEntries.map((e: any) => (
                        <tr key={e.id} className="border-t border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)]">
                          <td className="p-2 text-[var(--color-text-muted)] whitespace-nowrap">
                            {new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td className="p-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                              parseFloat(e.amount) >= 0
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                            }`}>
                              {e.event_type.replace(/_/g, ' ')}
                            </span>
                          </td>
                          <td className={`p-2 text-right font-mono font-medium ${
                            parseFloat(e.amount) >= 0 ? 'text-green-600' : 'text-red-500'
                          }`}>
                            {fmt(parseFloat(e.amount))}
                          </td>
                          <td className="p-2 font-mono text-[var(--color-text-muted)]">
                            {e.ml_order_id || '-'}
                          </td>
                          <td className="p-2 text-[var(--color-text-secondary)] max-w-[200px] truncate" title={e.description}>
                            {e.description || '-'}
                          </td>
                          <td className="p-2 text-[var(--color-text-muted)]">{e.source || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {/* Pagination */}
                <div className="p-3 border-t border-[var(--color-border)]">
                  <Pagination
                    page={ledgerPag.page}
                    totalPages={ledgerPag.totalPages}
                    total={ledgerPag.total}
                    perPage={ledgerPag.perPage}
                    onPageChange={ledgerPag.setPage}
                    onPerPageChange={ledgerPag.setPerPage}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function BigCard({ icon, label, value, color }: { icon: React.ReactNode; label: string; value: string; color: string }) {
  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-4">
      <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
        <span className="w-5 h-5">{icon}</span>
        <p className="text-xs">{label}</p>
      </div>
      <p className={`text-xl font-bold mt-2 ${color}`}>{value}</p>
    </div>
  );
}

function StatCard({ label, value, color, icon }: { label: string; value: string | number; color?: string; icon?: React.ReactNode }) {
  return (
    <div className="bg-[var(--color-bg-card)] border border-[var(--color-border)] rounded-lg p-3">
      <div className="flex items-center gap-1">
        {icon}
        <p className="text-xs text-[var(--color-text-secondary)]">{label}</p>
      </div>
      <p className={`text-lg font-bold mt-1 ${color || 'text-[var(--color-text)]'}`}>{value}</p>
    </div>
  );
}
